import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { PaperAirplaneIcon } from '@heroicons/react/16/solid'
import type { IPrimer } from '../../../../Model/index.js'
import type { Taxas } from '../../../../Model/ITaxa.js'
import type { BaseModalProps } from '../../../Modal/BaseModal.js'
import { BaseModal } from '../../../Modal/BaseModal.js'
import { tracingAxios } from '../../../../Repository/Sentry.js'
import { Environment } from '../../../../environment.js'
import { addMessage } from '../../../../Flux/Slice/Application/ApplicationSlice.js'
import { errorMessage, successMessage } from '../../../../Model/Message/IMessage.js'

export interface PrimerAmpliconMismatchModalProps {
  /** The amplicon the order targets. */
  expectedAmplicon: Taxas
  /** The known primer(s) whose sequences matched, but which are registered for a different amplicon. */
  foundPrimers: IPrimer[]
}

export const PrimerAmpliconMismatchModal = (props: PrimerAmpliconMismatchModalProps & BaseModalProps) => {
  const dispatch = useDispatch()
  const [submitting, setSubmitting] = useState(false)
  const [requested, setRequested] = useState<Set<string>>(new Set())

  const foundAmplicons = Array.from(new Set(props.foundPrimers.map((p) => p.amplicon).filter(Boolean)))

  const requestForOrderAmplicon = async (primer: IPrimer, key: string) => {
    setSubmitting(true)
    try {
      await tracingAxios.post(
        `${Environment.API_URL}/v1/primer/`,
        {
          amplicon: props.expectedAmplicon,
          forwardName: primer.forwardName,
          forwardSequence: primer.forwardSequence,
          reverseName: primer.reverseName,
          reverseSequence: primer.reverseSequence,
          reference: primer.reference,
          referenceLink: primer.referenceLink,
          basePairLength: primer.basePairLength
        },
        { withCredentials: true }
      )
      dispatch(
        addMessage(
          successMessage(
            `Thank you! We received your request to register this primer for ${props.expectedAmplicon}. We will contact you once it is in our database.`
          )
        )
      )
      setRequested((prev) => new Set(prev).add(key))
    } catch (error) {
      dispatch(
        addMessage(
          errorMessage('Could not submit the primer request. ' + (error instanceof Error ? error.message : 'Please try again later.'))
        )
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <BaseModal isOpen={props.isOpen} onClose={props.onClose} testId={'primer-amplicon-mismatch-modal'}>
      <div className="modal-box w-full max-w-xl">
        <h3 className="text-lg font-bold">This primer is for a different amplicon</h3>

        <div className="mt-3 rounded border border-amber-400 bg-amber-50 p-3 text-amber-800 text-sm">
          We recognized your primer sequences, but they are registered in our database for a different amplicon than this order targets. The
          order was <span className="font-bold">not</span> submitted.
        </div>

        <div className="mt-4 text-sm">
          <div className="flex justify-between gap-4">
            <span className="font-bold">This order targets:</span>
            <span>{props.expectedAmplicon}</span>
          </div>
          <div className="mt-1 flex justify-between gap-4">
            <span className="font-bold">Your primer is registered for:</span>
            <span>{foundAmplicons.length > 0 ? foundAmplicons.join(', ') : 'a different amplicon'}</span>
          </div>
        </div>

        <div className="mt-4 text-sm text-gray-600">
          Please check that the target taxonomy for this order is correct and that you selected the right samples. If this primer should also
          be valid for {props.expectedAmplicon}, you can request that we register it for that amplicon:
        </div>

        {props.foundPrimers.length > 0 && (
          <ul className="mt-3 space-y-2 text-sm">
            {props.foundPrimers.map((p, i) => {
              const key = p.id ?? `${p.forwardSequence}-${p.reverseSequence}-${i}`
              const isRequested = requested.has(key)
              return (
                <li key={key} className="flex items-center justify-between gap-3 rounded border border-gray-200 p-2">
                  <span>
                    {p.forwardName} / {p.reverseName} — <span className="font-mono">{p.amplicon}</span>
                    {p.reference ? ` (${p.reference})` : ''}
                  </span>
                  <button
                    className="btn btn-sm btn-primary whitespace-nowrap"
                    disabled={submitting || isRequested}
                    onClick={() => requestForOrderAmplicon(p, key)}
                  >
                    <PaperAirplaneIcon className="h-4 w-4 -rotate-45" />
                    {isRequested ? 'Requested' : `Request for ${props.expectedAmplicon}`}
                  </button>
                </li>
              )
            })}
          </ul>
        )}

        <div className="modal-action">
          <button className="btn btn-outline" onClick={() => props.onClose()}>
            Close
          </button>
        </div>
      </div>
    </BaseModal>
  )
}
